import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import {
  FolderKanban, LogOut, Menu, MoreHorizontal, Search,
  Settings, SlidersHorizontal, Users, X,
} from 'lucide-react';
import clsx from 'clsx';
import GoToPalette from '../components/GoToPalette';
import { NotificationBell, type BellClients } from '../components/NotificationBell';
import type { DestGroup } from '../components/GoToPalette';
import { ActiveClientProvider, useActiveClient } from '../contexts/ActiveClientContext';
import { ClientSwitcher } from './ClientSwitcher';
import { aaiApi } from '../services/aaiApi';
import { getPartnerOnboarding, logoutUser } from '../services/api';
import { useImpersonation } from '../contexts/ImpersonationContext';

/*
 * The Solution Partner console.
 *
 * Four destinations and one client. Dashboard and Clients span every assigned
 * company; Projects, Results, and Client Context read the one the switcher
 * names, and no screen under this shell shows anything that is not scoped to
 * it. That is why the switcher sits in the chrome rather than on a page: the
 * answer to "whose work am I looking at" must never scroll away.
 *
 * The shell is the employee portal's, deliberately: same 224px sidebar, same
 * kit, same ⌘K palette. A partner and an employee are looking at different
 * work, not at a different product.
 *
 * A super working as a partner lands in here too, and the only way out used to
 * be Sign out, which ends the borrowed session and the super's own with it:
 * they were dropped at the login screen having to sign in again. So the chrome
 * carries the way back, and the account menu drops Sign out entirely while it
 * is somebody else's account to sign out of. Who you are borrowing is under
 * the name in the sidebar.
 */

interface NavItem {
  path: string;
  label: string;
  icon: React.ElementType;
  desc: string;
  count?: number;
}

const initialsOf = (name: string): string => {
  const parts = (name || '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[1][0]).toUpperCase();
};

// Pages that read the active client carry its name in the crumb, so the
// answer to "whose projects" is on the page even when the switcher is off-screen.
const CLIENT_CRUMB: Record<string, string> = {
  '/partner/client': 'Overview',
  // '/partner/engagement': 'Engagement',
  '/partner/projects': 'Projects',
  '/partner/schedule': 'Schedule',
  '/partner/context': 'Client Context',
};

const Shell: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { clients, active, setActive, loading, error } = useActiveClient();
  const [goToOpen, setGoToOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [navOpen, setNavOpen] = useState(false);
  const [projectCount, setProjectCount] = useState(0);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const { impersonationState, endImpersonation } = useImpersonation();
  const borrowed = impersonationState.isImpersonating;
  const bellClients = useMemo<BellClients>(() => ({
    nameOf: (id) => clients.find((c) => c.id === id)?.name,
    open: (id) => {
      if (id !== active?.id && clients.some((c) => c.id === id)) setActive(id);
    },
  }), [clients, active?.id, setActive]);

  const userName = localStorage.getItem('user_name') || 'Solution Partner';
  const userEmail = localStorage.getItem('user_email') || '';

  // A partner who arrived through an invite link has a welcome deck ahead of
  // them. It is seen once; finishing or skipping it stamps the partner, so
  // this check stops sending them there.
  useEffect(() => {
    setNavOpen(false);
  }, [location.pathname, location.search]);

  useEffect(() => {
    if (!navOpen) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setNavOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [navOpen]);

  useEffect(() => {
    let live = true;
    getPartnerOnboarding()
      .then((me) => { if (live && !me.onboarded) navigate('/partner/welcome', { replace: true }); })
      .catch(() => { /* an unreadable state never blocks the console */ });
    return () => { live = false; };
  }, [navigate]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'k' || !(e.metaKey || e.ctrlKey)) return;
      e.preventDefault();
      setGoToOpen(true);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  useEffect(() => {
    if (!menuOpen) return;
    const onDown = (e: PointerEvent) => {
      if (menuRef.current?.contains(e.target as Node)) return;
      setMenuOpen(false);
    };
    document.addEventListener('pointerdown', onDown);
    return () => document.removeEventListener('pointerdown', onDown);
  }, [menuOpen]);

  // The projects count is work-in-hand, so it is read from the same rows the
  // page lists rather than from a separate counter that could drift.
  const activeId = active?.id;
  useEffect(() => {
    if (!activeId) return;
    let live = true;
    aaiApi.listRequests()
      .then((requests) => {
        if (!live) return;
        setProjectCount(requests.data.requests.filter((r) => r.status !== 'declined').length);
      })
      .catch(() => { /* the badge simply stays at its last value */ });
    return () => { live = false; };
    // Keyed on the id, not the object: the client only changes when its id
    // does, and re-running this on every render would refetch forever.
  }, [activeId]);

  /* Dashboard and Engagement are parked for now: the Intake framework, which
     used to live under Settings, is the partner's first stop instead. */
  const nav: NavItem[] = [
    // { path: '/partner/dashboard', label: 'Dashboard', icon: LayoutDashboard, desc: 'What is happening across your clients, and what needs you' },
    { path: '/partner/framework', label: 'Champion Framework', icon: SlidersHorizontal, desc: 'The client intake every member of your clients answers' },
    { path: '/partner/clients', label: 'Clients', icon: Users, desc: 'Every client you look after, with their status' },
    // { path: '/partner/engagement', label: 'Engagement', icon: KanbanSquare, desc: 'The selected client\u2019s journey, with each Orbit conversation as a ticket' },
    { path: '/partner/projects', label: 'Projects', icon: FolderKanban, desc: 'Every piece of work for the selected client', count: projectCount },
  ];

  const isActive = (path: string): boolean => location.pathname === path || location.pathname.startsWith(`${path}/`);

  const clientCrumb = CLIENT_CRUMB[location.pathname];
  // Intake framework and Clients span every client, so there is no client to
  // switch there: a client is chosen by opening it from the list.
  const showSwitcher = Boolean(clientCrumb);
  const crumbs: string[] = (() => {
    // if (location.pathname.startsWith('/partner/dashboard')) return ['Dashboard'];
    if (location.pathname.startsWith('/partner/framework')) return ['Champion Framework'];
    if (!clientCrumb) return ['Clients'];
    if (!active) return ['Clients', clientCrumb];
    return ['Clients', active.name, clientCrumb];
  })();

  const destGroups: DestGroup[] = [
    {
      key: 'pages',
      label: 'Pages',
      items: [
        ...nav.map((item) => ({ label: item.label, desc: item.desc, path: item.path, current: isActive(item.path) })),
        // { label: 'Client Context', desc: 'Approved facts, sources, owners, and activity', path: '/partner/context', current: isActive('/partner/context') },
      ],
    },
    {
      key: 'actions',
      label: 'Actions',
      items: [
        { label: 'Add client', desc: 'Set up a new client company', path: '/partner/clients/new' },
        // { label: 'Schedule', desc: 'Sessions and calls for the selected client', path: '/partner/schedule' },
        // Documents hidden for now.
        // { label: 'Client documents', desc: 'Files for the selected client, and adding one', path: '/partner/client#documents' },
      ],
    },
  ];

  // On a phone the sidebar is a drawer opened from the topbar, the same as
  // the client shell (BLK-129): as a strip it stacked bars above the page.
  let drawerState = '-translate-x-full';
  if (navOpen) drawerState = 'translate-x-0 shadow-[0_12px_32px_rgba(15,23,42,0.18)]';

  return (
    <div className="min-h-screen bg-canvas font-sans">
      {navOpen && (
        <div aria-hidden className="fixed inset-0 z-[110] bg-black/30 sm:hidden" onClick={() => setNavOpen(false)} />
      )}
      <aside className={clsx(
        'sidebar fixed left-0 top-0 z-[120] flex h-full w-[280px] max-w-[85vw] flex-col border-r border-sidebar-border bg-white px-3.5 pb-3.5 pt-[21px] text-sidebar-text transition-transform duration-200 sm:z-[100] sm:w-[224px] sm:max-w-none sm:translate-x-0 sm:bg-sidebar-bg',
        drawerState,
      )}>
        <Link
          to="/partner/framework"
          aria-label="Agents Anywhere — home"
          className="block shrink-0 rounded-act py-2 transition-colors hover:bg-sidebar-hover"
        >
          <img src="/brand/aa-logo-vector.png" alt="Agents Anywhere" className="brand-wordmark h-auto w-[151px] object-contain" />
        </Link>

        <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
          <p className="m-0 mb-2 mt-[22px] block text-[11px] font-semibold uppercase tracking-[0.14em] text-sidebar-heading">
            Workspace
          </p>
          <nav className="block">
            {nav.map((item) => {
              const Icon = item.icon;
              const on = isActive(item.path);
              const hasCount = typeof item.count === 'number' && item.count > 0;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  title={item.label}
                  className={clsx(
                    'aa-navitem relative mb-2 flex h-[42px] items-center gap-3 rounded-act px-[11px] text-[13px] transition-colors',
                    on && 'bg-sidebar-active font-semibold text-sidebar-text-active',
                    !on && 'font-medium text-sidebar-text hover:bg-sidebar-hover hover:text-sidebar-text-active',
                  )}
                >
                  {on && (
                    <span
                      aria-hidden
                      className="absolute bottom-1.5 left-[-8px] top-1.5 w-[3px] rounded-r bg-ink"
                    />
                  )}
                  <Icon size={18} strokeWidth={1.75} className="shrink-0" />
                  <span className="flex-1">{item.label}</span>
                  {hasCount && (
                    <span className={clsx(
                      'ml-auto grid h-[21px] min-w-[21px] place-items-center rounded-full px-2 font-sans text-[11px] tabular-nums',
                      on && 'bg-accent-500 text-black',
                      !on && 'bg-sidebar-hover text-sidebar-text',
                    )}>{item.count}</span>
                  )}
                </Link>
              );
            })}
          </nav>

        </div>

        <div className="block">
          {/* Client Context is off the sidebar for now: the Orbit Room moved to
              the Dashboard and the Company tools and Activity tabs are commented out.
          <Link
            to="/partner/context"
            title="Client Context"
            className={clsx(
              'relative flex h-11 min-w-0 flex-1 items-center gap-3 rounded-act px-3 text-[13px] transition-colors sm:mb-0 sm:h-[42px] sm:flex-none sm:px-[10px]',
              isActive('/partner/context') && 'bg-sidebar-active font-semibold text-sidebar-text-active',
              !isActive('/partner/context') && 'font-medium text-sidebar-text hover:bg-sidebar-hover hover:text-sidebar-text-active',
            )}
          >
            {isActive('/partner/context') && (
              <span aria-hidden className="absolute -left-2 bottom-1.5 top-1.5 hidden w-[3px] rounded-r bg-ink sm:block" />
            )}
            <Database size={18} strokeWidth={1.75} className="shrink-0" />
            <span className="truncate">Client Context</span>
          </Link>
          */}

          <div ref={menuRef} className="relative mt-[14px] shrink-0">
            {menuOpen && (
              <div className="absolute bottom-full left-0 right-0 mb-2 overflow-hidden rounded-xl border border-gray-200 bg-white text-gray-900 shadow-[0_12px_32px_rgba(15,23,42,0.18)]">
                <div className="border-b border-gray-100 px-3 py-2.5">
                  <div className="truncate text-[13px] font-semibold">{userName}</div>
                  {userEmail && <div className="truncate font-sans text-[11px] text-gray-500">{userEmail}</div>}
                </div>
                <div className="py-1">
                  <Link
                    to="/partner/settings"
                    onClick={() => setMenuOpen(false)}
                    className="flex w-full items-center gap-2.5 px-3 py-2 text-left text-[12.5px] text-gray-600 transition-colors hover:bg-gray-50 hover:text-gray-950"
                  >
                    <Settings size={15} className="text-gray-400" />
                    Settings
                  </Link>
                </div>
                <div className="border-t border-gray-100 py-1">
                  {/* Signing out of an account you are only borrowing ends your
                      own session too, so while this is somebody else's account
                      the way out is handing it back, not logging out of it. */}
                  {borrowed && (
                    <button
                      type="button"
                      onClick={() => { setMenuOpen(false); endImpersonation(); }}
                      className="flex w-full items-center gap-2.5 px-3 py-2 text-left text-[12.5px] text-gray-600 transition-colors hover:bg-gray-50 hover:text-gray-950"
                    >
                      <X size={15} className="text-gray-400" />
                      Exit impersonation
                    </button>
                  )}
                  {!borrowed && (
                    <button
                      type="button"
                      onClick={() => logoutUser('/login')}
                      className="flex w-full items-center gap-2.5 px-3 py-2 text-left text-[12.5px] text-gray-600 transition-colors hover:bg-gray-50 hover:text-gray-950"
                    >
                      <LogOut size={15} className="text-gray-400" />
                      Sign out
                    </button>
                  )}
                </div>
              </div>
            )}

            <button
              type="button"
              onClick={() => setMenuOpen((open) => !open)}
              aria-haspopup="menu"
              aria-expanded={menuOpen}
              title={`${userName} — account, settings and sign out`}
              className={clsx(
                'flex h-[45px] w-full items-start gap-3 rounded-lg px-[9px] pt-[10px] text-left transition-colors hover:bg-sidebar-hover',
                menuOpen && 'bg-sidebar-active',
              )}
            >
              <div className="grid h-[34px] w-[34px] shrink-0 place-items-center rounded-tile bg-ink text-[12px] font-bold text-white">
                {initialsOf(userName)}
              </div>
              <span className="block min-w-0 flex-1">
                <span className="block truncate text-[13px] font-semibold text-sidebar-text">{userName}</span>
                <span className="block truncate text-[11px] text-sidebar-text">
                  {borrowed ? 'Working as' : 'Account and sign out'}
                </span>
              </span>
              <MoreHorizontal size={18} className="shrink-0 text-sidebar-text" />
            </button>
          </div>
        </div>
      </aside>

      <main className="min-h-screen bg-canvas sm:ml-[224px]">
        <header className="topbar sticky top-0 z-[80] flex h-16 shrink-0 items-center gap-2 border-b border-line bg-[#FFFFFFE0] px-4 backdrop-blur sm:gap-3.5 sm:px-[30px]">
          <button
            type="button"
            onClick={() => setNavOpen(true)}
            aria-label="Open menu"
            aria-expanded={navOpen}
            className="inline-flex h-[34px] w-[34px] shrink-0 items-center justify-center rounded-act border border-line bg-white text-ink-control transition-colors hover:bg-canvas-inset sm:hidden"
          >
            <Menu size={17} />
          </button>
          <div className="flex min-w-0 items-center gap-3">
            {showSwitcher && (
              <>
                <span className="hidden shrink-0 text-[12.5px] text-ink-dim md:block">Assigned client</span>
                <ClientSwitcher clients={clients} active={active} loading={loading} onChange={setActive} />
              </>
            )}
            <nav aria-label="Breadcrumb" className="hidden min-w-0 items-center gap-1.5 lg:flex">
              {crumbs.map((crumb, i) => (
                <React.Fragment key={crumb}>
                  {i > 0 && <span aria-hidden className="shrink-0 text-[11px] text-ink-soft">/</span>}
                  <span
                    aria-current={i === crumbs.length - 1 ? 'page' : undefined}
                    className={clsx(
                      'truncate py-1 text-[11px] leading-[14.5px]',
                      i === crumbs.length - 1 && crumbs.length > 1 && 'font-semibold text-ink',
                      (i < crumbs.length - 1 || crumbs.length === 1) && 'text-ink-muted',
                    )}
                  >
                    {crumb}
                  </span>
                </React.Fragment>
              ))}
            </nav>
          </div>

          <div className="flex-1" />

          {/* The same yellow pill the client shell carries, in the same corner.
              It is one control doing one job, so it should not look like two
              different things depending on whose account you are in. */}
          {borrowed && (
            <button
              type="button"
              onClick={endImpersonation}
              title="Exit impersonation"
              aria-label="Exit impersonation"
              className="inline-flex h-[34px] shrink-0 items-center justify-center gap-1.5 rounded-full bg-accent-500 px-2.5 text-[12.5px] font-semibold text-gray-900 shadow-sm transition-all hover:brightness-95 sm:h-10 sm:px-4 whitespace-nowrap"
            >
              <X size={15} /> <span className="hidden sm:inline">Exit impersonation</span>
            </button>
          )}

          <button
            type="button"
            onClick={() => setGoToOpen(true)}
            title="Go to (⌘K)"
            className="inline-flex h-[34px] w-[34px] shrink-0 items-center justify-center gap-2 rounded-act border border-line bg-white text-[12px] text-ink-control transition-colors hover:bg-canvas-inset sm:w-auto sm:px-[9px]"
          >
            <Search size={15} className="shrink-0" />
            <span className="hidden sm:inline">Go to</span>
            <kbd className="hidden shrink-0 rounded border border-line bg-white px-1.5 py-0.5 font-sans text-[10.5px] text-ink-soft sm:inline">⌘K</kbd>
          </button>

          {/*
          <Link
            to="/partner/context?tab=activity"
            title="Activity"
            className="inline-flex h-[34px] w-[34px] shrink-0 items-center justify-center gap-2 rounded-act border border-line bg-white text-[12px] font-semibold text-ink-control transition-colors hover:bg-canvas-inset sm:w-auto sm:px-[10px]"
          >
            <Clock size={15} className="shrink-0" />
            <span className="hidden sm:inline">Activity</span>
          </Link>
          */}

          {/* Every client's notices, not only the one the switcher names, so a
              rejected key at another client is not missed. There is no
              partner-wide inbox page, so the panel is the whole surface. */}
          <NotificationBell scope="partner" clients={bellClients} />
        </header>

        <div className="page-content mx-5 pb-20 pt-8 sm:mx-[30px] sm:pt-10">
          {error && (
            <div className="mx-auto mb-5 max-w-[1200px] rounded-act border border-danger/25 bg-danger-soft px-4 py-3 text-[13px] text-danger">
              {error}
            </div>
          )}
          <Outlet />
        </div>
      </main>

      {goToOpen && (
        <GoToPalette groups={destGroups} roleLabel="Solution Partner" onClose={() => setGoToOpen(false)} />
      )}
    </div>
  );
};

const SolutionPartnerLayout: React.FC = () => (
  <ActiveClientProvider>
    <Shell />
  </ActiveClientProvider>
);

export default SolutionPartnerLayout;
